import React, { useEffect, useState } from 'react'
import Card from '../../components/Card'
import Checkbox from '../../components/Checkbox'
import Table from '../../components/table'
import texts from '../../texts'

const EXCLUDED_CATEGORIES = ['hiragana', 'katakana']

const GlobalReview = ({ vocabFile, setVocabFile, darkMode = false }) => {
  const [allCategories, setAllCategories] = useState([])
  const [selectedCategories, setSelectedCategories] = useState([])
  const [data, setData] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [finished, setFinished] = useState(false)
  const [started, setStarted] = useState(false)
  const [showResultsDirect, setShowResultsDirect] = useState(false)
  const [sortCategory, setSortCategory] = useState('none') // 'none' | 'asc' | 'desc'
  // const sortedData = [...data].sort((a, b) => {
  //   if (!a.category) return 1
  //   if (!b.category) return -1
  //   if (a.category < b.category) return -1
  //   if (a.category > b.category) return 1
  //   return 0
  // })

  const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[array[i], array[j]] = [array[j], array[i]]
    }
    return array
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`/data/${vocabFile}`)
        const jsonData = await response.json()
        const categories = Object.keys(jsonData).filter((cat) => !EXCLUDED_CATEGORIES.includes(cat))
        setAllCategories(categories)
        setSelectedCategories(categories)
      } catch (error) {
        console.error('Error al cargar las categorías:', error)
      }
    }
    fetchData()
  }, [vocabFile])

  useEffect(() => {
    if (!started) return
    const fetchWords = async () => {
      try {
        const response = await fetch(`/data/${vocabFile}`)
        const jsonData = await response.json()
        let allWords = []
        const seen = new Set()
        selectedCategories.forEach((category) => {
          const catData = jsonData[category]
          if (!catData) return

          // Si todos los valores son objetos, es anidado (subgrupos)
          if (Object.values(catData).every((val) => typeof val === 'object')) {
            Object.entries(catData).forEach(([subcat, subObj]) => {
              Object.entries(subObj).forEach(([hiragana, romaji]) => {
                const key = `${hiragana}|||${romaji}|||${subcat}`
                if (!seen.has(key)) {
                  seen.add(key)
                  allWords.push({
                    hiragana,
                    romaji,
                    correct: null,
                    category,
                    subcat // <-- Añadido
                  })
                }
              })
            })
          } else {
            // Categoría plana
            Object.entries(catData).forEach(([hiragana, romaji]) => {
              const key = `${hiragana}|||${romaji}`
              if (!seen.has(key)) {
                seen.add(key)
                allWords.push({
                  hiragana,
                  romaji,
                  correct: null,
                  category,
                  subcat: category // <-- Así siempre hay subcat
                })
              }
            })
          }
        })
        setData(shuffleArray(allWords))
        setCurrentIndex(0)
        setFinished(false)
      } catch (error) {
        console.error('Error al cargar los datos:', error)
      }
    }
    if (selectedCategories.length > 0) {
      fetchWords()
    } else {
      setData([])
    }
  }, [selectedCategories, started, vocabFile])

  const handleCategoryChange = (cat) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    )
  }

  const handleAnswer = (isCorrect) => {
    const updatedData = [...data]
    updatedData[currentIndex].correct = isCorrect ? '○' : '×'
    if (currentIndex + 1 < data.length) {
      setCurrentIndex((prev) => prev + 1)
    } else {
      setFinished(true)
    }
  }

  const handleRestart = () => {
    setFinished(false)
    setStarted(false)
    setCurrentIndex(0)
  }

  // Función para cambiar el estado de ordenación en bucle
  const handleSortCategory = () => {
    setSortCategory((prev) => (prev === 'none' ? 'asc' : prev === 'asc' ? 'desc' : 'none'))
  }

  // Prepara los datos según el estado de ordenación
  let dataToShow = data
  if (sortCategory === 'asc') {
    dataToShow = [...data].sort((a, b) => {
      if (!a.category) return 1
      if (!b.category) return -1
      if (a.category < b.category) return -1
      if (a.category > b.category) return 1
      return 0
    })
  } else if (sortCategory === 'desc') {
    dataToShow = [...data].sort((a, b) => {
      if (!a.category) return 1
      if (!b.category) return -1
      if (a.category < b.category) return 1
      if (a.category > b.category) return -1
      return 0
    })
  }

  const columns = [
    { Header: 'Palabra', accessor: 'hiragana' },
    { Header: 'Traducción', accessor: 'romaji' },
    { Header: 'Categoría', accessor: 'category', sortable: true },
    { Header: 'Subcategoría', accessor: 'subcat' },
    {
      Header: 'Resultado',
      accessor: 'correct',
      Cell: ({ value }) => (
        <span
          className={
            value === '○' || value === '✔'
              ? 'solo-correct'
              : value === '×' || value === '✖'
                ? 'solo-incorrect'
                : ''
          }
        >
          {value === '○' || value === '✔' || value === '×' || value === '✖' ? value : '-'}
        </span>
      )
    }
  ]

  return (
    <div style={{ paddingTop: '30px' }}>
      {!started ? (
        <>
          <div style={{ marginBottom: 20 }}>
            <strong>Selecciona el archivo de vocabulario:</strong>
            <select
              value={vocabFile}
              onChange={(e) => setVocabFile(e.target.value)}
              style={{ marginLeft: 12, padding: 6, fontSize: '1em' }}
            >
              <option value='vocabulario.json'>vocabulario.json</option>
              <option value='vocabulario_unidad.json'>vocabulario_unidad.json</option>
            </select>
          </div>
          <div style={{ marginBottom: 20 }}>
            <strong>Selecciona las categorías a repasar:</strong>
          </div>
          <Checkbox
            categories={allCategories}
            selected={selectedCategories}
            onChange={handleCategoryChange}
          />
          <button
            onClick={() => setStarted(true)}
            disabled={selectedCategories.length === 0}
            style={{
              marginTop: 16,
              marginRight: 12,
              padding: '10px 24px',
              fontSize: '1.1em',
              background: selectedCategories.length === 0 ? '#ccc' : '#0974f1',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: selectedCategories.length === 0 ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s'
            }}
          >
            Empezar repaso
          </button>
          <button
            onClick={() => {
              setShowResultsDirect(true)
              setStarted(true)
              setFinished(true)
            }}
            disabled={selectedCategories.length === 0}
            style={{
              marginTop: 16,
              padding: '10px 24px',
              fontSize: '1.1em',
              background: selectedCategories.length === 0 ? '#ccc' : '#181a1b',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: selectedCategories.length === 0 ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s'
            }}
          >
            {texts.flip.verResults}
          </button>
        </>
      ) : !finished && !showResultsDirect && data.length > 0 ? (
        <>
          <div style={{ fontWeight: 'bold', marginBottom: '12px', fontSize: '1.1em' }}>
            {currentIndex + 1} / {data.length}
          </div>
          <Card
            key={`${vocabFile}-${selectedCategories.join(',')}-${currentIndex}`}
            hiragana={data[currentIndex].hiragana}
            romaji={data[currentIndex].romaji}
            subcat={data[currentIndex].subcat}
            onAnswer={handleAnswer}
            darkMode={darkMode}
          />
          <button
            onClick={() => {
              setShowResultsDirect(true)
              setStarted(true)
              setFinished(true)
            }}
            disabled={selectedCategories.length === 0}
            style={{
              marginTop: 16,
              marginLeft: 12,
              padding: '10px 24px',
              fontSize: '1.1em',
              background: selectedCategories.length === 0 ? '#ccc' : '#181a1b',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: selectedCategories.length === 0 ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s'
            }}
          >
            {texts.flip.verResults}
          </button>
        </>
      ) : (
        (finished || showResultsDirect) && (
          <div style={{ maxWidth: 700, margin: 'auto' }}>
            <h2>{texts.flip.resultados}</h2>
            {dataToShow.length > 70 && (
              <button
                onClick={handleRestart}
                style={{
                  marginBottom: '20px',
                  padding: '10px 24px',
                  fontSize: '1.1em',
                  background: '#181a1b',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
              >
                {texts.flip.reiniciar}
              </button>
            )}
            <div style={{ width: '100%', overflowX: 'auto' }}>
              <Table
                columns={columns}
                data={dataToShow}
                onSortCategory={handleSortCategory}
                sortCategory={sortCategory}
              />
            </div>
            <button
              onClick={handleRestart}
              style={{
                marginTop: '20px',
                padding: '10px 24px',
                fontSize: '1.1em',
                background: '#181a1b',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer'
              }}
            >
              Reiniciar
            </button>
          </div>
        )
      )}
    </div>
  )
}

export default GlobalReview
